import { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import {
  CalendarDays,
  PlusCircle,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  X,
  ChevronDown,
} from 'lucide-react'
import {
  getLeaveTypes,
  getLeaveQuota,
  getMyLeaves,
  createLeave,
  cancelLeave,
  getPendingLeaves,
  approveLeave,
  rejectLeave,
  LeaveType,
  LeaveQuota,
  LeaveRequest,
  CreateLeaveData,
  PendingLeaveRequest,
} from '../../services/eagleVision'
import { useSearchParams } from 'react-router-dom'
import PullToRefresh from '../../components/common/PullToRefresh'
import { usePermissions } from '../../hooks/usePermissions'

type TabType = 'my_leaves' | 'approvals'
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected'

const STATUS_BADGE: Record<string, string> = {
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  pending: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-gray-100 text-gray-500',
}

export default function LeavePage() {
  const { can } = usePermissions()
  const canCreate = can('pwa_leave', 'create')
  const canCancel = can('pwa_leave', 'cancel')
  const canApprove = can('pwa_leave', 'approve')

  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabType>(
    searchParams.get('tab') === 'approvals' && canApprove ? 'approvals' : 'my_leaves'
  )

  // Follow the ?tab= param when navigating here from a notification link.
  useEffect(() => {
    if (searchParams.get('tab') === 'approvals' && canApprove) {
      setActiveTab('approvals')
    }
  }, [searchParams, canApprove])

  // Quota state
  const [leaveQuotas, setLeaveQuotas] = useState<LeaveQuota[]>([])
  const [quotaLoading, setQuotaLoading] = useState(false)

  // My Leaves state
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const [myLeaves, setMyLeaves] = useState<LeaveRequest[]>([])
  const [myLeavesLoading, setMyLeavesLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [showLeaveForm, setShowLeaveForm] = useState(false)
  const [leaveForm, setLeaveForm] = useState<CreateLeaveData>({
    leave_type_id: 0,
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
    half_day_type: undefined,
  })
  const [submitting, setSubmitting] = useState(false)
  const [cancelling, setCancelling] = useState<number | null>(null)

  // Approvals state
  const [pendingLeaves, setPendingLeaves] = useState<PendingLeaveRequest[]>([])
  const [approvalsLoading, setApprovalsLoading] = useState(false)
  const [approvalFilter, setApprovalFilter] = useState<'pending' | 'all'>('pending')
  const [actionModal, setActionModal] = useState<{
    type: 'approve' | 'reject'
    leave: PendingLeaveRequest
  } | null>(null)
  const [actionRemarks, setActionRemarks] = useState('')
  const [actionSubmitting, setActionSubmitting] = useState(false)

  // Shared state
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    fetchMyLeaves()
    fetchLeaveTypes()
    fetchLeaveQuota()
  }, [])

  useEffect(() => {
    if (activeTab === 'approvals' && canApprove) {
      fetchPendingLeaves()
    }
  }, [activeTab, approvalFilter])

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000)
      return () => clearTimeout(t)
    }
  }, [success])

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 6000)
      return () => clearTimeout(t)
    }
  }, [error])

  const fetchLeaveQuota = async () => {
    setQuotaLoading(true)
    try {
      const quotas = await getLeaveQuota()
      setLeaveQuotas(quotas)
    } catch {
      // non-critical — quota section just won't show
    } finally {
      setQuotaLoading(false)
    }
  }

  const fetchLeaveTypes = async () => {
    try {
      const types = await getLeaveTypes()
      setLeaveTypes(types)
    } catch {
      // non-critical
    }
  }

  const fetchMyLeaves = async () => {
    setMyLeavesLoading(true)
    try {
      const res = await getMyLeaves(statusFilter === 'all' ? undefined : statusFilter)
      setMyLeaves(res.data || [])
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load leave requests')
    } finally {
      setMyLeavesLoading(false)
    }
  }

  useEffect(() => {
    fetchMyLeaves()
  }, [statusFilter])

  const fetchPendingLeaves = async () => {
    setApprovalsLoading(true)
    try {
      const res = await getPendingLeaves(approvalFilter)
      setPendingLeaves(res.data || [])
    } catch (err: any) {
      if (err?.response?.status !== 403) {
        setError(err?.response?.data?.message || 'Failed to load leave approvals')
      }
    } finally {
      setApprovalsLoading(false)
    }
  }

  const handleCreateLeave = async () => {
    if (!leaveForm.leave_type_id || !leaveForm.start_date || !leaveForm.end_date) {
      setError('Please fill in all required fields')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await createLeave(leaveForm)
      setSuccess('Leave request submitted successfully')
      setShowLeaveForm(false)
      setLeaveForm({ leave_type_id: 0, start_date: '', end_date: '', reason: '', is_half_day: false, half_day_type: undefined })
      fetchMyLeaves()
      fetchLeaveQuota()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to submit leave request')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelLeave = async (leave: LeaveRequest) => {
    setCancelling(leave.id)
    try {
      const ids = leave.leave_ids && leave.leave_ids.length > 0 ? leave.leave_ids : [leave.id]
      for (const lid of ids) {
        await cancelLeave(lid)
      }
      setSuccess('Leave request cancelled')
      fetchMyLeaves()
      fetchLeaveQuota()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to cancel leave request')
    } finally {
      setCancelling(null)
    }
  }

  const handleApprovalAction = async () => {
    if (!actionModal) return
    setActionSubmitting(true)
    try {
      const leave = actionModal.leave
      const dateDisplay = leave.start_date === leave.end_date
        ? formatDate(leave.start_date)
        : `${formatDate(leave.start_date)} - ${formatDate(leave.end_date)}`
      const ctx = {
        leaveIds: leave.leave_ids,
        employee_id: leave.employee?.id,
        leave_type_name: leave.leave_type?.name,
        date_display: dateDisplay,
      }
      if (actionModal.type === 'approve') {
        await approveLeave(leave.id, actionRemarks || undefined, ctx)
        setSuccess('Leave request approved')
      } else {
        await rejectLeave(leave.id, actionRemarks || undefined, ctx)
        setSuccess('Leave request rejected')
      }
      setActionModal(null)
      setActionRemarks('')
      fetchPendingLeaves()
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to ${actionModal.type} leave request`)
    } finally {
      setActionSubmitting(false)
    }
  }

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchMyLeaves(), fetchLeaveQuota()])
  }, [statusFilter])

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    try {
      return format(new Date(dateStr), 'MMM d, yyyy')
    } catch {
      return dateStr
    }
  }

  const filteredLeaves = myLeaves

  return (
    <PullToRefresh onRefresh={refreshAll}>
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-6 w-6 text-primary-600" />
          <h1 className="text-xl font-bold text-gray-900">Leave</h1>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Tab Bar */}
      <div className="bg-gray-100 p-1 rounded-lg flex mb-4">
        <button
          onClick={() => setActiveTab('my_leaves')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'my_leaves' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          }`}
        >
          My Leaves
        </button>
        {canApprove && (
          <button
            onClick={() => setActiveTab('approvals')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'approvals' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            Approvals
          </button>
        )}
      </div>

      {/* ==================== My Leaves Tab ==================== */}
      {activeTab === 'my_leaves' && (
        <div>
          {/* Leave Quota Summary */}
          {quotaLoading ? (
            <div className="flex items-center justify-center py-4 mb-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : leaveQuotas.length > 0 && (
            <div className="mb-4 grid grid-cols-2 gap-2">
              {leaveQuotas.map((q) => (
                <div
                  key={q.leave_type_id}
                  className="bg-white border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: q.color || '#6B7280' }}
                    />
                    <span className="text-xs font-medium text-gray-700 truncate">{q.name}</span>
                  </div>
                  {q.quota > 0 ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-gray-900">
                          {q.remaining ?? 0}
                        </span>
                        <span className="text-xs text-gray-400">/ {q.quota}</span>
                      </div>
                      <div className="mt-1.5 w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, ((q.used) / q.quota) * 100)}%`,
                            backgroundColor: q.color || '#6B7280',
                          }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                        <span>{q.used} used</span>
                        {q.pending > 0 && <span>{q.pending} pending</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-gray-900">{q.used}</span>
                        <span className="text-xs text-gray-400">used</span>
                      </div>
                      {q.pending > 0 && (
                        <p className="text-[10px] text-gray-400 mt-1">{q.pending} pending</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Request Leave Button */}
          {canCreate && (
            <button
              onClick={() => setShowLeaveForm(!showLeaveForm)}
              className="w-full mb-4 flex items-center justify-center gap-2 py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              <PlusCircle className="h-5 w-5" />
              Request Leave
            </button>
          )}

          {/* Leave Request Form */}
          {showLeaveForm && (
            <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">New Leave Request</h3>

              {/* Leave Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                <div className="relative">
                  <select
                    value={leaveForm.leave_type_id}
                    onChange={(e) => setLeaveForm({ ...leaveForm, leave_type_id: Number(e.target.value) })}
                    className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-3 pr-10 text-sm focus:border-primary-500 focus:ring-primary-500 appearance-none"
                  >
                    <option value={0}>Select leave type</option>
                    {leaveTypes.map((lt) => (
                      <option key={lt.id} value={lt.id}>{lt.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={leaveForm.start_date}
                    onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value, end_date: leaveForm.end_date || e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 py-2.5 px-3 text-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={leaveForm.end_date}
                    min={leaveForm.start_date}
                    onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 py-2.5 px-3 text-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Half Day Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={leaveForm.is_half_day}
                  onChange={(e) => setLeaveForm({ ...leaveForm, is_half_day: e.target.checked, half_day_type: e.target.checked ? leaveForm.half_day_type : undefined })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Half Day</span>
              </label>

              {leaveForm.is_half_day && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Half Day Type</label>
                  <div className="relative">
                    <select
                      value={leaveForm.half_day_type || ''}
                      onChange={(e) => setLeaveForm({ ...leaveForm, half_day_type: (e.target.value || undefined) as 'first_half' | 'second_half' | undefined })}
                      className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-3 pr-10 text-sm focus:border-primary-500 focus:ring-primary-500 appearance-none"
                    >
                      <option value="">Select half</option>
                      <option value="first_half">First Half</option>
                      <option value="second_half">Second Half</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={leaveForm.reason}
                  onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                  rows={2}
                  placeholder="Optional reason for leave..."
                  className="block w-full rounded-lg border border-gray-300 py-2.5 px-3 text-sm focus:border-primary-500 focus:ring-primary-500 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleCreateLeave}
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Submit
                </button>
                <button
                  onClick={() => setShowLeaveForm(false)}
                  className="py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Status Filter Pills */}
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {(['all', 'pending', 'approved', 'rejected'] as StatusFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  statusFilter === f
                    ? 'bg-primary-100 text-primary-700 border border-primary-300'
                    : 'bg-gray-100 text-gray-600 border border-transparent'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Leave List */}
          {myLeavesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : filteredLeaves.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarDays className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No leave requests</p>
              <p className="text-sm mt-1">Your leave requests will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLeaves.map((leave) => (
                <div key={leave.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{leave.leave_type?.name || 'Leave'}</p>
                      <p className="text-sm text-gray-500">
                        {formatDate(leave.start_date)}
                        {leave.end_date && leave.end_date !== leave.start_date && ` - ${formatDate(leave.end_date)}`}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[leave.status] || STATUS_BADGE.pending}`}>
                      {leave.status}
                    </span>
                  </div>
                  {leave.is_half_day && (
                    <span className="inline-block px-2 py-0.5 mb-2 bg-blue-50 text-blue-600 rounded text-xs font-medium">
                      Half Day
                    </span>
                  )}
                  {leave.reason && (
                    <p className="text-sm text-gray-600 mb-2">{leave.reason}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{leave.days || 1} day{(leave.days || 1) > 1 ? 's' : ''}</span>
                    {leave.status === 'pending' && canCancel && (
                      <button
                        onClick={() => handleCancelLeave(leave)}
                        disabled={cancelling === leave.id}
                        className="flex items-center gap-1 text-red-500 hover:text-red-700 font-medium"
                      >
                        {cancelling === leave.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <XCircle className="h-3 w-3" />
                        )}
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== Approvals Tab ==================== */}
      {activeTab === 'approvals' && canApprove && (
        <div>
          {/* Status Filter */}
          <div className="flex gap-2 mb-4">
            {(['pending', 'all'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setApprovalFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  approvalFilter === f
                    ? 'bg-primary-100 text-primary-700 border border-primary-300'
                    : 'bg-gray-100 text-gray-600 border border-transparent'
                }`}
              >
                {f === 'all' ? 'All' : 'Pending'}
              </button>
            ))}
          </div>

          {/* Pending Leave List */}
          {approvalsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : pendingLeaves.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No leave requests</p>
              <p className="text-sm mt-1">
                {approvalFilter === 'pending' ? 'No pending leave requests to review' : 'No leave requests found'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingLeaves.map((leave) => (
                <div key={leave.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  {/* Employee Info */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{leave.employee?.name}</p>
                      <p className="text-xs text-gray-500">
                        {[leave.employee?.department, leave.employee?.designation].filter(Boolean).join(' - ')}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[leave.status] || STATUS_BADGE.pending}`}>
                      {leave.status}
                    </span>
                  </div>

                  {/* Leave Details */}
                  <div className="mb-3 space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <CalendarDays className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{leave.leave_type?.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>
                        {formatDate(leave.start_date)}
                        {leave.end_date && leave.end_date !== leave.start_date && ` - ${formatDate(leave.end_date)}`}
                        {' '}({leave.days || 1} day{(leave.days || 1) > 1 ? 's' : ''})
                        {leave.is_half_day && <span className="ml-1 text-blue-600">(Half Day)</span>}
                      </span>
                    </div>
                  </div>

                  {leave.reason && (
                    <p className="text-sm text-gray-600 mb-3 bg-gray-50 p-2 rounded">{leave.reason}</p>
                  )}

                  {/* Approve / Reject Buttons */}
                  {leave.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setActionModal({ type: 'approve', leave }); setActionRemarks('') }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => { setActionModal({ type: 'reject', leave }); setActionRemarks('') }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== Approve/Reject Confirmation Modal ==================== */}
      {actionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {actionModal.type === 'approve' ? 'Approve Leave' : 'Reject Leave'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {actionModal.leave.employee?.name} - {actionModal.leave.leave_type?.name}
              </p>
            </div>
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {actionModal.type === 'approve' ? 'Remarks (optional)' : 'Reason (optional)'}
              </label>
              <textarea
                value={actionRemarks}
                onChange={(e) => setActionRemarks(e.target.value)}
                rows={3}
                placeholder={actionModal.type === 'approve' ? 'Add optional remarks...' : 'Add reason for rejection...'}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:border-primary-500 focus:ring-primary-500 resize-none"
              />
            </div>
            <div className="p-4 border-t flex gap-2">
              <button
                onClick={handleApprovalAction}
                disabled={actionSubmitting}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${
                  actionModal.type === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {actionModal.type === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
              <button
                onClick={() => { setActionModal(null); setActionRemarks('') }}
                disabled={actionSubmitting}
                className="py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PullToRefresh>
  )
}
